(ns frontend.view.nev.kyc.kyc-profile
    (:require
      [basic-components.field-editor :as field-editor]
      [common.intelligence.common :as ic]
      [common.schema.entities.kyc.options :as kyc-options]
      [common.languages :as languages]
      [common.localization :as loc]
      [common.schema.entities.workspace.kyc.kyc-process :as kyc-process-schema]
      [common.schema.registry :as registry]
      [frontend.actions.common :as common-actions]
      [frontend.actions.entity :as entity-actions]
      [frontend.actions.kyc-profile :as kyc-actions]
      [frontend.actions.screening-profile :as sp-actions]
      [frontend.db :as app-db]
      [frontend.logging :as logging]
      [frontend.page-components.default-page-layout :as default-page-layout]
      [frontend.page-components.zap-toolbar :as zap-toolbar]
      [frontend.routing :as routing]
      [frontend.state.current-session :as session]
      [frontend.tenant-customizations :as tenant-customizations]
      [frontend.utils :as fu]
      [frontend.view.entity-viewers.components :as nev-components]
      [frontend.view.entity-viewers.entity-editor :as entity-editor]
      [frontend.view.entity-viewers.event-pane :as event-pane]
      [frontend.view.entity-viewers.modules :as modules]
      [frontend.view.entity-viewers.utils :as entity-utils]
      [frontend.view.modals.confirmation.view :as confirmation-modal]
      [frontend.view.modals.events :as modals]
      [frontend.view.nev.events :as nev-events]
      [frontend.view.nev.kyc.events :as events]
      [frontend.view.not-found :as not-found]
      [frontend.view.syncing-data :as syncing]
      [frontend.zap-time :as zap-time]
      [potpuri.core :as p]
      [reagent.core :as r]
      [ui.components.button.view :as button]
      [ui.editors.labelled-input.view :as labelled-input]
      [ui.util :as uu]))

(defn- kyc-grid-route
  []
  [(if (session/new-grid-2023?)
     :kyc-grid
     :kyc-grid-old)])

(defn blocking-configuration-toolbar-item
  [{:keys [value entity ord]}]
  (let [label (kyc-options/get-blocking-status-label value)
        selected? (-> entity
                      :kyc-profile/blocking-status
                      (or :blocking-status/calculated)
                      (= value))]
    {:id               value
     :ord              ord
     :label            (str value)
     :dropdown-action? true
     :custom-item?     true
     :value            [:div
                        {:class "flex justify-between gap-2 text-sm"}
                        [:div
                         {:class "flex items-center"}
                         [:div
                          {:class "min-w-5"}
                          (when selected?
                            [:i {:class "fas fa-circle text-xs m-0"}])]

                         [:span label]]]
     :on-select        (fn []
                         (entity-actions/*->field-update!
                           (:db/id entity)
                           :kyc-profile/blocking-status
                           value
                           (fn [_resp])
                           (fn [err]
                             (js/console.log "Error updating blocking conf " err))))}))

(defn blocking-configuration-toolbar-items
  [{:keys [entity]}]
  [{:ord              5
    :divider?         true
    :dropdown-action? true}
   {:ord              6
    :divider          true
    :dropdown-action? true
    :value            "Blocking configuration"
    :label            "Blocking configuration"}
   (blocking-configuration-toolbar-item
     {:ord    7
      :value  :blocking-status/calculated
      :entity entity})
   (blocking-configuration-toolbar-item
     {:ord    8
      :value  :blocking-status/blocked
      :entity entity})
   (blocking-configuration-toolbar-item
     {:ord    9
      :value  :blocking-status/not-blocked
      :entity entity})])

(defn toolbar-buttons
  [{:keys [entity] :as view-args}]
  (let [entity-id (:db/id entity)
        flagged? (contains? (:account/flagged-entities @session/account)
                            entity-id)]
    (concat
      [(zap-toolbar/dropdown-button
         {:icon      :fa-star
          :on-select (partial common-actions/toggle-flag entity flagged?)
          :ord       1
          :label     (loc/tr (if flagged? :unflag-entity-menu-item :flag-entity-menu-item))})]
      (when-not (session/is-read-only-user?)
        [(zap-toolbar/dropdown-button
           {:icon      :fa-cog
            :on-select events/configure-KYC-categories
            :ord       2
            :label     (loc/tr :configure-kyc-categories)})])
      (when-not (:common/disabled? entity)
        [(zap-toolbar/delete-button entity (kyc-grid-route))])

      [(merge
         (nev-components/audit-trail-button {:entity     entity
                                             :entity-ids [entity-id]})
         {:ord 4})]

      (blocking-configuration-toolbar-items view-args))))

(defn refresh-screening-status
  [kyc-profile-id state-atom]
  (swap! state-atom dissoc :loaded? :active? :reason :error?)
  (kyc-actions/*->get-screening-statuses
    {:kyc-profile-ids [kyc-profile-id]}
    (fn [response]
      (let [{:keys [active? reason]} (-> response
                                         :body
                                         :statuses
                                         (get kyc-profile-id))]
        (swap! state-atom assoc :loaded? true :active? active? :reason reason)))
    (fn [error]
      (swap! state-atom assoc :error? true)
      (js/console.log "Error while getting screening status " error))))

(defn change-screening-status!
  [new-value entity on-cancel screening-state-atom]
  (let [info-text
        (case new-value
          :screening-status/auto (loc/tr :screening-status-change-auto-desc)
          :screening-status/enforced (loc/tr :screening-status-change-enforced-desc)
          :screening-status/suspended (loc/tr :screening-status-change-suspended-desc))]
    (modals/open-modal!
      confirmation-modal/modal-key
      {:body           [:div
                        [:span (loc/tr :screening-status-changing-to)]
                        [:b (str (loc/tr new-value) ". ")]
                        [:span info-text]]
       :confirm-text   "Change screening status"
       :confirm-icon   ""
       :skip-checkbox? true
       :on-confirm     #(entity-actions/optimistic-field-update
                          (:db/id entity)
                          :kyc-profile/screening-status
                          new-value
                          session/entities
                          (fn []
                            (refresh-screening-status (:db/id entity) screening-state-atom))
                          (fn [err]
                            (logging/error "Failed to update Screening status: "
                                           err)))
       :on-cancel      on-cancel})))


(defn blocking-status-field
  [{:keys [entity]}]
  (let [state* (r/atom {:loading-screening-profiles? true})]
    (r/create-class
      {:component-did-mount (fn []
                              (sp-actions/*->get-screening-profiles-for-kyc-profile
                                {:kyc-profile-id (:db/id entity)}
                                (fn [resp]
                                  (let [screening-profiles-by-id (->> resp
                                                                      :body
                                                                      :screening-profiles
                                                                      (p/index-by :db/id))]
                                    (swap! app-db/entities merge screening-profiles-by-id)
                                    (swap! state* assoc :loading-screening-profiles? false)))
                                (fn [err]
                                  (js/console.log "Error" err))))
       :reagent-render
       (fn [{:keys [entity]}]
         (let [tenant @session/tenant
               tenant-schema @session/tenant-schema
               manual-blocking-status (get entity :kyc-profile/blocking-status :blocking-status/calculated)
               calculated-blocking-status (fu/get-value tenant tenant-schema entity :kyc-profile/calc-blocking-status)
               blocked? (= calculated-blocking-status :blocking-status/blocked)
              status-label (kyc-options/get-blocking-status-label calculated-blocking-status)
               info-label (if (= manual-blocking-status :blocking-status/calculated)
                            (loc/tr :calculated-lc)
                            (loc/tr :manual-lc))
               loading? (:loading-screening-profiles? @state*)]
           [:div
            {:class (uu/cm "flex flex-col gap-1.5"
                           (when loading? "min-w-40"))}
            [:label
             {:class "text-sm m-0 font-bold"}
             (loc/tr :blocking-status)]

            (when (or (not loading?)
                      (not= manual-blocking-status :blocking-status/calculated))
              [:div
               {:class (uu/cm "rounded-full border border px-3 py-1 flex items-center gap-2"
                              (if blocked?
                                "border-danger-regular bg-danger-regular text-danger-regular-text"
                                "bg-primary-regular text-primary-regular-text"))}
               [:span
                {:class "font-medium"}
                status-label]
               [:span
                {:class "text-sm m-0 font-medium"}
                (str "(" info-label ")")]])]))})))


(defn kyc-status-expiry
  [{:keys [entity entity-atom screening-state-atom] :as _args}]
  (let [tenant @session/tenant
        tenant-schema @session/tenant-schema
        approval-date (:kyc-profile/approval-date entity)
        calculated-deadline (fu/get-value tenant tenant-schema entity :kyc-profile/calculated-deadline)
        expires (or calculated-deadline 0)
        expires-interval-str (zap-time/long->interval-str (- expires (zap-time/now)))
        documents-valid-until (or (:kyc-profile/documents-valid-until entity) 0)
        docs-valid-interval-str (zap-time/long->interval-str (- documents-valid-until
                                                                (zap-time/now)))]
    [:div
     {:class "p-3 w-full flex flex-wrap justify-between border border-solid border-base-light-border-paler rounded"}
     [:div
      {:class "px-3 mb-3 flex gap-2 items-center"}
      [:label
       (str (loc/tr :kyc-profile/status) " : ")]
      [:span
       {:class "bg-success-light text-success-light-text rounded-full py-1 px-2 font-bold text-xs"}
       (->> entity
            :kyc-profile/status
            (kyc-options/label))]]
     [field-editor/view
      {:editor/type :editor.type/date
       :draggable?  false
       :class-name  "mb-3 w-auto me-4"
       :value       approval-date
       :state-atom  entity-atom
       :attribute   :kyc-profile/approval-date
       :label       (loc/tr :kyc-profile/approval-date)
       :clearable?  false
       :read-only?  (session/is-read-only-user?)
       :on-save!    (fn [_attribute new-value]
                      (modals/open-modal!
                        confirmation-modal/modal-key
                        {:body           (loc/tr :update-approval-date-confirm-text)
                         :confirm-text   (loc/tr :update-approval-date)
                         :confirm-icon   ""
                         :skip-checkbox? true
                         :on-confirm     #(entity-actions/optimistic-field-update
                                            (:db/id entity)
                                            :kyc-profile/approval-date
                                            new-value
                                            session/entities
                                            (constantly nil)
                                            (fn [err]
                                              (logging/error "Failed to update Approval date : "
                                                             err)))}))}]

     (when (session/automated-kyc-renewal?)
       [:<>
        ;; Maybe enabled later if we continue kyc renewal
        #_[:div
           {:class "flex flex-col justify-center mb-2.5"}
           [labelled-input/view
            {:data-type   :data.type/date
             :label       (loc/tr :kyc-profile/next-renewal-start-date)
             :value       (:kyc-profile/next-renewal-start-date entity)
             :on-change   (fn [value]
                            (entity-actions/optimistic-field-update
                              (:db/id entity)
                              :kyc-profile/next-renewal-start-date
                              value
                              session/entities
                              (fn []
                                (js/console.log "Success"))
                              (fn [err]
                                (js/console.log "Error updating renewal date" err))))
             :date-format (:tenant/date-format tenant)}]]

        [:div
         {:class "flex flex-col justify-center mb-2.5"}
         [labelled-input/view
          {:data-type :data.type/keyword
           :options   [{:value :true
                        :label (loc/tr :true)}
                       {:value :false
                        :label (loc/tr :false)}]
           :value     (if (:kyc-profile/auto-reminders-enabled? entity)
                        :true
                        :false)
           :label     (loc/tr :kyc-profile/auto-reminders-enabled?)
           :on-change (fn [value-kw]
                        (entity-actions/optimistic-field-update
                          (:db/id entity)
                          :kyc-profile/auto-reminders-enabled?
                          (if (= value-kw :true) true false)
                          session/entities
                          (fn [])
                          (fn [err]
                            (js/console.log "Error updating reminders enabled" err))))}]]])

     [field-editor/view
      {:editor/type :editor.type/date
       :draggable?  false
       :class-name  "mb-3 w-auto"
       :value       calculated-deadline
       :read-only?  true
       :state-atom  entity-atom
       :attribute   :kyc-profile/calculated-deadline
       :label       (loc/tr :kyc-profile/calculated-deadline)
       :clearable?  false}]

     (when (session/screening?)
       (let [field-schema (registry/get-field-schema tenant-schema :kyc-profile/screening-status)
             {:keys [active? reason loaded?]} @screening-state-atom
             screening-status (or (:kyc-profile/screening-status entity)
                                  :screening-status/auto)
             show-info-text? (= screening-status :screening-status/auto)
             info-text (if active?
                         (loc/tr :screening-is-active)
                         (loc/tr :screening-is-not-active))
             show-tooltip? (not= reason :unknown)
             info-tooltip-text (loc/tr reason)]
         [:div
          {:style {:width               "250px"
                   :margin-top          (if show-info-text? "19.5px" "2px")
                   :transition-duration "200ms"}}
          [:div
           {:style {:width "200px"}}
           [field-editor/view
            {:editor/type  :editor.type/select
             :attribute    :kyc-profile/screening-status
             :options      (:options field-schema)
             :drag-options {:draggable? false}
             :class-name   "mb-3 w-auto"
             :value        screening-status
             :read-only?   false
             :on-save!     (fn [_ new-value-str clear-cb!]
                             (clear-cb!)
                             (let [value-kw (-> new-value-str
                                                (subs 1)
                                                (keyword))]
                               (change-screening-status!
                                 value-kw
                                 entity
                                 (fn []
                                   (js/console.log "cancel"))
                                 screening-state-atom)))
             :label        (-> field-schema
                               :field/translations
                               :en)
             :clearable?   false}]]
          [:div
           {:class "ml-4 flex gap-2 items-center"
            :style {:max-height          (if show-info-text?
                                           "100px"
                                           "0px")
                    :overflow            (when-not show-info-text? "hidden")
                    :transition-duration "200ms"
                    :height              "20px"}}
           (when loaded?
             [:<>
              [fu/tooltip-overlay-if
               {:id        "timeline-selection-tooltip"
                :placement "bottom"}
               show-tooltip?
               info-tooltip-text
               [:i.fas.fa-info-circle
                {:class (uu/cm "text-danger-light-text"
                               (when active? "text-base-regular-text"))}]]
              [:p
               {:class "text-xs"}
               info-text]])]]))

     [:div
      {:class "mt-[19.5px]"}
      [blocking-status-field
       {:entity entity}]]

     (when (> expires 0)
       [:div.ml-5.d-flex.align-items-center
        [:label.field-editor__content__header__label__text-content.mr-3
         (str (loc/tr :kyc-profile/next-deadline) " : ")]
        [:span
         {:class-name (fu/cs "aggregate-value"
                             (when (and expires (< expires (zap-time/now)))
                               "salmon"))}
         (case (:kyc-profile/status entity)
           :kyc-status/not-started "-"
           :kyc-status/missing "-"
           :kyc-status/expired expires-interval-str
           :kyc-status/valid expires-interval-str
           :kyc-status/valid-with-expired-docs expires-interval-str)]])
     (when (> documents-valid-until 0)
       [:div.ml-5.d-flex.align-items-center
        [:label.field-editor__content__header__label__text-content.mr-3
         (str (loc/tr :kyc-documents-validity) " : ")]
        [:span
         {:class-name (fu/cs "aggregate-value"
                             (when (and documents-valid-until
                                        (< documents-valid-until (zap-time/now)))
                               "salmon"))}
         docs-valid-interval-str]])]))

(defn kyc-processes
  [{:keys [entity] :as _args}]
  (let [processes (->> entity
                       :kyc-profile/kyc-processes
                       (keep (partial get @session/entities)))]
    [:div
     (when-not (session/is-read-only-user?)
       [:div.flex.px-3.pt-2
        (if-not (number? (:kyc-profile/their-kyc-contact entity))
          (fu/tooltip (loc/tr :please-select-their-kyc-contact)
                      [:div.disabled-button
                       (loc/tr :add- [(loc/tr :entity.type/kyc-process)])])
          [button/primary-solid
           {:disabled? (not (number? (:kyc-profile/their-kyc-contact entity)))
            :on-click  (partial events/create-process! {:entity entity})}
           (loc/tr :add- [(loc/tr :entity.type/kyc-process)])])])
     (when (seq processes)
       [:div.grid.ps-3.py-3
        [:div.table-responsive
         [:table.zap-table.zap-table--striped
          {:style {:width "100%"}}
          [:thead.zap-table__head
           [:tr.zap-table__row
            [:th.zap-table__headline (loc/tr :kyc-process/status)]
            [:th.zap-table__headline (loc/tr :kyc-process/template)]
            [:th.zap-table__headline (loc/tr :kyc-process/language)]
            [:th.zap-table__headline (loc/tr :kyc-process/invitee-email)]
            [:th.zap-table__headline (loc/tr :kyc-process/invitee-phone)]
            [:th.zap-table__headline (loc/tr :common/created-on)]
            [:th.zap-table__headline (loc/tr :common/updated-on)]]]
          [:tbody.zap-table__body
           (->>
             processes
             (sort-by :common/created-on)
             (reverse)
             (map (fn [kyc-process]
                    [:tr
                     {:key        (str "KYC-process-" (:db/id kyc-process)
                                       "-" (pr-str (:kyc-process/status kyc-process)))
                      :class-name (fu/cs "zap-table__row" (pr-str (:kyc-process/status kyc-process)))
                      :on-click   (fn [_]
                                    (when-not (session/is-read-only-user?)
                                      (routing/open-entity (:db/id kyc-process))))}
                [:td.zap-table__cell (kyc-options/process-status-label (:kyc-process/status kyc-process))]
                     [:td.zap-table__cell
                      (kyc-process-schema/template-label (:kyc-process/template kyc-process))]
                     [:td.zap-table__cell (languages/get-label (:kyc-process/language kyc-process))]
                     [:td.zap-table__cell (:kyc-process/invitee-email kyc-process)]
                     [:td.zap-table__cell (:kyc-process/invitee-phone kyc-process)]
                     [:td.zap-table__cell (fu/format-date-ts (:common/created-on kyc-process))]
                     [:td.zap-table__cell (fu/format-date-ts (:common/updated-on kyc-process))]]))
             (doall))]]]])]))

(defn kyc-profile-view
  [{:keys [kyc-profile-id] :as _view-args}]
  (let [state-atom (r/cursor app-db/app-state [:views :nev :kyc-profile])
        initialized? (r/atom false)
        temp-atom (r/cursor state-atom [:temp])
        screening-state-atom (r/atom nil)
        view-state-atom (r/cursor state-atom [:view-state])
        error-atom (r/cursor state-atom [:error])
        notification-atom (r/cursor state-atom [:notification])
        events-pane-atom (r/cursor state-atom [:events-pane])
        entity-atom (r/cursor session/entities [kyc-profile-id])]
    (r/create-class
      {:component-did-update
       (partial entity-utils/component-did-update
                :kyc-profile-id
                (partial nev-events/clear-errors! error-atom notification-atom))
       :component-did-mount
       (fn [_]
         (entity-utils/reset-state! state-atom kyc-profile-id
                                    {:always-fetch? (session/new-grid-2023?)})
         (reset! initialized? true)

         (when (session/screening?)
           (refresh-screening-status kyc-profile-id screening-state-atom)))

       :component-will-unmount
       (fn [_]
         (nev-events/clear-errors! error-atom notification-atom)
         (entity-utils/clear-state! state-atom))
       :reagent-render
       (fn [{:keys [kyc-profile-id] :as view-args}]
         (let [kyc-profile (get @session/entities kyc-profile-id)
               enriched-entity (ic/enrich-entity kyc-profile)]
           (cond
             (not @initialized?)
             [syncing/view]
             (or (:common/disabled? kyc-profile)
                 (:not-found @state-atom))
             [not-found/not-found-content]
             (:loading @state-atom)
             [syncing/view]
             :else
             (if (or (not kyc-profile)
                     (:common/disabled? kyc-profile))
               [:div " "]
               (let [capman-hidden-fields #{:kyc-profile/ubo-validation
                                            :kyc-profile/investor-classification
                                            :kyc-profile/customer-status
                                            :kyc-profile/termination-date}
                     header-rows (cond->> [;[[:kyc-profile/target :investor/description]]
                                           [:kyc-profile/our-kyc-contact :kyc-profile/their-kyc-contact]
                                           ;[:kyc-profile/our-contact :kyc-profile/their-contact]
                                           [:kyc-profile/tax-countries :kyc-profile/risk-category]
                                           [:kyc-profile/ubo-validation :kyc-profile/kyc-country]
                                           [:kyc-profile/investor-classification :kyc-profile/termination-date]
                                           [:kyc-profile/customer-status]]
                                          (tenant-customizations/capman?)
                                          (mapv (fn [row]
                                                  (filterv #(not (capman-hidden-fields %)) row)))
                                          (tenant-customizations/capman?)
                                          (filterv seq))
                     entity-template (entity-editor/resolve-template
                                       {:entity                @entity-atom
                                        :tenant-schema         @session/tenant-schema
                                        :account               @session/account
                                        :role                  @session/role
                                        :pipeline-id           nil
                                        :remove-fields         []
                                        :override-editor-types nil})
                     entity-template-lookup (->> entity-template
                                                 (map (fn [field-schema]
                                                        [(:attribute field-schema)
                                                         field-schema]))
                                                 (into {}))
                     view-id :kyc-profile]
                 [:<>
                  (when-not (session/is-guest?)
                    [zap-toolbar/view state-atom
                     (partial zap-toolbar/toolbar-back (kyc-grid-route) (loc/tr :back-to/kyc-grid))
                     nil
                     (toolbar-buttons (assoc view-args
                                        :entity
                                        kyc-profile))])
                  [:div.entity__container
                   (when @error-atom
                     [nev-components/error-bar error-atom])
                   (when @notification-atom
                     [nev-components/notification-bar notification-atom])

                   [:div.entity
                    [:div
                     {:class "w-full max-w-[1920px] mx-auto p-4"}

                     [kyc-status-expiry
                      {:component-state      temp-atom
                       :view-state           view-state-atom
                       :entity               kyc-profile
                       :enriched-entity      enriched-entity
                       :entity-atom          entity-atom
                       :screening-state-atom screening-state-atom}]

                     [nev-components/header-module
                      {:component-state        temp-atom
                       :view-state             view-state-atom
                       :entity                 kyc-profile
                       :enriched-entity        enriched-entity
                       :entity-atom            entity-atom
                       :entity-template-lookup entity-template-lookup
                       :field-rows             header-rows}]
                     [:div.md:grid.grid-cols-10.gap-4
                      [:div
                       {:class (if (session/is-guest?)
                                 "col-span-10"
                                 "col-span-10 lg:col-span-5")}
                       [kyc-processes
                        {:component-state temp-atom
                         :view-state      view-state-atom
                         :entity          kyc-profile
                         :enriched-entity enriched-entity
                         :entity-atom     entity-atom}]]
                      [:div
                       ;; Temporary fix. Don't know how make it work inside component
                       {:class "col-span-10 lg:col-span-5 [&_.fa-grip-vertical]:hidden"}
                       [modules/view
                        {:view-id             view-id
                         :ordering            (get-in @session/view-config [view-id :modules :ordering])
                         :save-modules-order! (partial events/save-modules-order!
                                                       (:entity/type kyc-profile))
                         :modules             [(nev-components/attachments-module view-id
                                                                                  :kyc-profile-attachments
                                                                                  kyc-profile)]}]
                       [event-pane/view
                        {:state             events-pane-atom
                         :primary-entity-id kyc-profile-id
                         :entity-ids        [kyc-profile-id]}]]]]]]])))))})))

(defmethod routing/render-view :entity.type/kyc-profile
  [{:keys [route-params _view-params]}]
  (let [kyc-profile-id (-> route-params
                           :id
                           js/parseInt)]
    [default-page-layout/view
     {:class-name "zap-page kyc-profile"}
     ;;the key change forces rerendering of the component (and its child components)
     ^{:key (str "kyc-profile-viewer-id-" kyc-profile-id)}
     [kyc-profile-view {:kyc-profile-id kyc-profile-id}]]))
